import { z } from 'zod';

import { httpClient } from '@/shared/api/httpClient';

import {
  orgRefSchema,
  registrationLinkSchema,
  teamSummarySchema,
  userRefSchema,
  usersPageSchema,
} from '../model/types';
import type {
  OrgRef,
  RegistrationLink,
  TeamSummary,
  UserAccountState,
  UserRef,
  UsersPage,
} from '../model/types';

/**
 * Czyste funkcje endpointów Identity — zero Reacta, zero stanu.
 *
 * ⚠ Uprawnienia sprawdza **serwer**, nie te funkcje: lista użytkowników i wszystkie zmiany
 * są `AdminOnly`, a lista zespołów jest dla każdego zalogowanego (spec 9.2: *„kto jest
 * w jakim zespole, to wiedza organizacyjna, nie sekret"*). Ukrywanie sekcji przed
 * nie-adminem to wygoda, nie kontrola dostępu.
 */

const teamListSchema = z.array(teamSummarySchema);
const memberListSchema = z.array(userRefSchema);
const orgListSchema = z.array(orgRefSchema);

/**
 * Katalog bytów organizacyjnych — **userzy i zespoły w jednym worku** (spec sek. 3).
 *
 * ⭐ Osobna trasa istnieje dlatego, że `/users` jest i zostaje `AdminOnly` (niesie mejle,
 * role i stan kont), a przynależność organizacyjna sekretem **nie jest** (spec 9.2).
 * Bez niej zwykły człowiek nie mógłby wskazać właściciela w omniboksie, bo nie miałby
 * prawa przeczytać żadnej listy ludzi — to była cała przyczyna decyzji 79.
 *
 * Pusta fraza jest poprawna: podpowiedzi mają działać od pierwszego znaku, a sufit
 * na rozmiar odpowiedzi nakłada serwer.
 */
export async function fetchOrg(q: string, signal?: AbortSignal): Promise<OrgRef[]> {
  const response = await httpClient.get<unknown>(`/org?q=${encodeURIComponent(q)}`, {
    ...(signal ? { signal } : {}),
  });

  return orgListSchema.parse(response);
}

/**
 * Strona listy kont. `state` = `undefined` znaczy „wszyscy" — **filtr, szukanie
 * i stronicowanie są serwerowe**.
 *
 * ⚠ Nie ma tu wariantu „pobierz wszystko": po imporcie kont bywają setki, a ekran, który
 * je wszystkie ściąga, przestaje się otwierać dokładnie u tego klienta, u którego jest
 * najbardziej potrzebny.
 */
export async function fetchUsers(
  params: {
    state?: UserAccountState | undefined;
    hasPassword?: boolean | undefined;
    q?: string;
    offset?: number;
    limit?: number;
  },
  signal?: AbortSignal,
): Promise<UsersPage> {
  const query = new URLSearchParams();

  if (params.state !== undefined) {
    query.set('state', params.state);
  }

  if (params.hasPassword !== undefined) {
    query.set('hasPassword', String(params.hasPassword));
  }

  if (params.q) {
    query.set('q', params.q);
  }

  if (params.offset) {
    query.set('offset', String(params.offset));
  }

  if (params.limit) {
    query.set('limit', String(params.limit));
  }

  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  const response = await httpClient.get<unknown>(`/users${suffix}`, {
    ...(signal ? { signal } : {}),
  });

  return usersPageSchema.parse(response);
}


/**
 * Zmiana loginu, czyli adresu e-mail konta.
 *
 * ⚠ To zmiana **tożsamości**, nie danych kontaktowych — unieważnia sesje tego konta.
 * ⚠ Nowy adres nie jest przez to zweryfikowany (weryfikacja przychodzi razem z pocztą).
 */
export async function changeUserLogin(userId: number, login: string): Promise<void> {
  await httpClient.put<void>(`/users/${userId}/login`, { login });
}

/**
 * Trzy przejścia stanu konta pod jedną trasą per akcja.
 *
 * ⭐ `activate` („Wpuść") i `enable` („Odblokuj") to **osobne akcje**, choć obie kończą się
 * kontem żywym: pierwsza jest wpuszczeniem do organizacji, druga cofnięciem blokady. Zlane
 * w jedną dawałyby obejście blokady tym samym przyciskiem, którym się wpuszcza.
 */
export type UserStateAction = 'activate' | 'disable' | 'enable';

export async function changeUserState(id: number, action: UserStateAction): Promise<void> {
  await httpClient.post<void>(`/users/${id}/${action}`, {});
}

/**
 * Ustawienie hasła przez admina.
 *
 * ⚠ To **jedyna** droga do zmiany hasła w produkcie: nie ma maili ani linków resetujących
 * (spec sek. 10, świadome ograniczenie MVP). Admin robi to serwerowo, po weryfikacji
 * tożsamości poza systemem.
 */
export async function setUserPassword(id: number, newPassword: string): Promise<void> {
  await httpClient.put<void>(`/users/${id}/password`, { newPassword });
}

export async function fetchTeams(signal?: AbortSignal): Promise<TeamSummary[]> {
  const response = await httpClient.get<unknown>('/teams', {
    ...(signal ? { signal } : {}),
  });

  return teamListSchema.parse(response);
}

export async function createTeam(name: string, key: string): Promise<TeamSummary> {
  const response = await httpClient.post<unknown>('/teams', { name, key });

  return teamSummarySchema.parse(response);
}

/**
 * Skład zespołu — **lekkie referencje**, bo to widok dla każdego zalogowanego (spec 9.2:
 * „kto jest w jakim zespole, to wiedza organizacyjna, nie sekret"), a nie panel admina.
 */
export async function fetchTeamMembers(
  teamId: number,
  signal?: AbortSignal,
): Promise<UserRef[]> {
  const response = await httpClient.get<unknown>(`/teams/${teamId}/members`, {
    ...(signal ? { signal } : {}),
  });

  return memberListSchema.parse(response);
}

/**
 * Zmiana nazwy **i klucza** zespołu.
 *
 * ⚠ Klucz jest tożsamością zespołu dla pliku importu, więc jego zmiana rozłącza zespół od
 * plików wskazujących stary symbol. Formularz mówi to wprost przy polu.
 */
export async function updateTeam(
  teamId: number,
  name: string,
  key: string,
): Promise<TeamSummary> {
  const response = await httpClient.patch<unknown>(`/teams/${teamId}`, { name, key });

  return teamSummarySchema.parse(response);
}

/**
 * ⛔ Serwer odmawia, gdy zespół jest właścicielem elementów — inaczej zostałaby wisząca
 * własność, a element pokazałby właściciela jako `#123`.
 */
export async function deleteTeam(teamId: number): Promise<void> {
  await httpClient.delete<void>(`/teams/${teamId}`);
}

/**
 * Dopisanie **kilku osób naraz**. Wsad jest niepodzielny po stronie serwera: jeden zły
 * identyfikator odrzuca całość, żeby zaznaczenie dziesięciu osób nie kończyło się cichym
 * dopisaniem dziewięciu.
 */
export async function addTeamMembers(teamId: number, userIds: readonly number[]): Promise<void> {
  await httpClient.post<void>(`/teams/${teamId}/members`, { userIds });
}

export async function addTeamMember(teamId: number, userId: number): Promise<void> {
  await httpClient.put<void>(`/teams/${teamId}/members/${userId}`, {});
}

export async function removeTeamMember(teamId: number, userId: number): Promise<void> {
  await httpClient.delete<void>(`/teams/${teamId}/members/${userId}`);
}

export async function fetchRegistrationLink(signal?: AbortSignal): Promise<RegistrationLink> {
  const response = await httpClient.get<unknown>('/registration/link', {
    ...(signal ? { signal } : {}),
  });

  return registrationLinkSchema.parse(response);
}

/** Odnowienie unieważnia poprzedni link — akcja nieodwracalna, stąd potwierdzenie w UI. */
export async function rotateRegistrationLink(): Promise<RegistrationLink> {
  const response = await httpClient.post<unknown>('/registration/link/rotate', {});

  return registrationLinkSchema.parse(response);
}

/**
 * Role konta — **pełny zestaw, nie różnica**.
 *
 * ⛔ Serwer odmawia, gdy admin odbiera rolę sobie albo ostatniemu aktywnemu adminowi:
 * to trzecia droga do tenanta bez żywego admina, obok dezaktywacji siebie i wyłączenia
 * ostatniego admina.
 */
export async function setUserRoles(userId: number, roles: readonly string[]): Promise<void> {
  await httpClient.put<void>(`/users/${userId}/roles`, { roles });
}

/** Poprawienie nazwy wyświetlanej przez admina — np. literówki z rejestracji. */
export async function updateUserProfile(userId: number, displayName: string): Promise<void> {
  await httpClient.patch<void>(`/users/${userId}`, { displayName });
}

/**
 * Zmiana **własnej** nazwy wyświetlanej. Odpowiedź niesie pełne „kim jestem", bo nazwa stoi
 * w powłoce i przy właścicielach — front i tak musiałby natychmiast odpytać o sesję.
 */
export async function updateOwnDisplayName(displayName: string): Promise<unknown> {
  return httpClient.patch<unknown>('/profile', { displayName });
}
